import React, { useEffect, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import Select from 'react-select'
import { constructOptions } from '../../Utils/helper';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { addClient, getAddType, getProvider, updateClient } from '../../Redux/Actions/DashOptions';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../Common/Loader';

const AddClient = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { id } = useParams()
    const location = useLocation();
    const [loading, setLoading] = useState(false)

    const { editClientData } = location.state || {};

    const getProviderData = useSelector((state) => state?.DashOptionsSlice?.listdata?.data)
    const getListingLoading = useSelector((state) => state?.DashOptionsSlice?.isLoading)
    const getDataType = useSelector((state) => state?.DashOptionsSlice?.getTypeData?.data)
    const getDataTypeLoading = useSelector((state) => state?.DashOptionsSlice?.isLoading)

    const options = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
    ]

    const handleSubmit = (values, form) => {
        const payload = {
            name: values.name,
            email: values?.email,
            phone: values?.phone,
            provider: values.provider?.value,
            type: values?.memberType?.value,
            status: values?.status?.value,
            address: values?.address || null,
            npi: values?.npi || null,
            zip_code: values?.zip_code || null,
            taxid: values?.taxid || null,
            username: values?.username || null,
            medicaidid: values?.medicaidid || null,
            password: values?.password || null
        }
        setLoading(true)

        if (!id) {
            dispatch(addClient(payload)).then((res) => {
                setLoading(false)

                if (res?.payload?.id) {
                    toast.success('Successfully added')
                    navigate('/dashboard/clients')
                } else {
                    toast.error('An error occured' || res?.message)
                }
                form.reset()
            })
        } else {
            dispatch(updateClient({ id: id, payload })).then((res) => {
                setLoading(false)
                if (res?.payload?.id) {
                    toast.success('Successfully updated')
                } else {
                    toast.error('An error occured')
                }
                form.reset()
                navigate('/dashboard/clients')
            })
        }
    };

    const handleBack = (e) => {
        e.preventDefault()
        if (id) {
            navigate("/dashboard/clients")
        } else {
            navigate("/dashboard");
        }
    };

    const validate = (values) => {
        const error = {};
        if (!values.name) {
            error.name = "Required"
        }
        if (!values.provider) {
            error.provider = "Required"
        }
        if (!values.memberType) {
            error.memberType = "Required"
        }
        if (!values.phone) {
            error.phone = "Required";
        } else if (!/^\d{10}$/.test(values.phone)) {
            error.phone = "Phone number must be exactly 10 digits";
        }
        if (!values.email) {
            error.email = "Required"
        }

        return error
    }

    const initialValues = useMemo(() => {
        if (id && editClientData) {
            return {
                name: editClientData?.name,
                email: editClientData?.email,
                phone: editClientData?.phone,
                memberType: { value: editClientData?.type?.id, label: editClientData?.type?.name },
                provider: { value: editClientData?.provider?.id, label: editClientData?.provider?.name },
                status: { value: editClientData?.status, label: editClientData?.status },
                address: editClientData?.address || '',
                npi: editClientData?.npi || '',
                zip_code: editClientData?.zip_code || '',
                taxid: editClientData?.taxid || '',
                username: editClientData?.username || '',
                medicaidid: editClientData?.medicaidid || '',
                password: editClientData?.password || ''
            }
        } return {
            name: '',
            email: "",
            phone: "",
            memberType: '',
            provider: '',
            status: '',
            address: '',
            npi: '',
            zip_code: '',
            taxid: '',
            username: '',
            medicaidid: '',
            password: ''
        }
    }, [editClientData]);

    useEffect(() => {
        dispatch(getProvider())
        dispatch(getAddType())
    }, [id])

    return (
        <div className="min-h-screen flex items-start justify-center pt-9 sm:px-6 lg:px-8 bg-gray-50">
            {loading && <Loader />}
            <div className="bg-white shadow-2xl border border-gray-100 rounded-xl px-10 py-8 w-full max-w-5xl">
                <h2 className="text-3xl font-bold text-center text-indigo-700 mb-6">
                    {id ? 'Edit Client' : 'Add New Client'}
                </h2>
                <Form
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={handleSubmit}
                    render={({ handleSubmit, valid }) => (
                        <form onSubmit={(e) => {
                            if (!valid) {
                                toast.error('Please fill all the mandatory fields')
                            };
                            handleSubmit(e)
                        }} className="space-y-5">
                            <div className="grid grid-cols-2 gap-4">
                                {/* Full Name */}
                                <Field name="name">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Full Name
                                                {meta.touched && meta.error && (
                                                    <span className="text-red-500 text-base font-bold ml-1">*</span>
                                                )}
                                            </label>
                                            <input
                                                {...input}
                                                type="text"
                                                placeholder="Enter Name"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}
                                </Field>

                                {/* Email */}
                                <Field name="email">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Email
                                                {meta.touched && meta.error && (
                                                    <span className="text-red-500 text-base font-bold ml-1">*</span>
                                                )}
                                            </label>
                                            <input
                                                {...input}
                                                type="email"
                                                placeholder="Enter Email"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}
                                </Field>

                                {/* Phone */}
                                <Field name="phone">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Phone
                                                {meta.touched && meta.error && (
                                                    <span className="text-red-500 text-base font-bold ml-1">*</span>
                                                )}
                                            </label>
                                            <input
                                                {...input}
                                                type="text"
                                                placeholder="Enter Phone Number"
                                                maxLength={10}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    if (val.length <= 10) {
                                                        input.onChange(val);
                                                    }
                                                }}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                            {meta.touched && meta.error && (
                                                <span className="text-red-500 text-base ml-1">
                                                    {meta?.error === "Phone number must be exactly 10 digits" ? meta.error : ""}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </Field>

                                 {/* Medicaid ID */}
                                <Field name="medicaidid">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Medicaid ID
                                            </label>
                                            <input
                                                {...input}
                                                type="text"
                                                placeholder="Enter Medicaid ID"
                                                maxLength={255}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}
                                </Field>


                                {/* NPI */}
                                <Field name="npi">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                NPI
                                            </label>
                                            <input
                                                {...input}
                                                type="text"
                                                placeholder="Enter NPI"
                                                maxLength={255}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}
                                </Field>

                                
                                {/* Tax ID */}
                                <Field name="taxid">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Tax ID
                                            </label>
                                            <input
                                                {...input}
                                                type="text"
                                                placeholder="Enter Tax ID"
                                                maxLength={255}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}
                                </Field>

                                {/* Username */}
                                <Field name="username">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Username
                                            </label>
                                            <input
                                                {...input}
                                                type="text"
                                                placeholder="Enter Username"
                                                maxLength={255}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}
                                </Field>

                               
                                {/* Password */}
                                <Field name="password">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Password
                                            </label>
                                            <input
                                                {...input}
                                                type="text"
                                                placeholder="Enter Password"
                                                maxLength={255}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}
                                </Field>

                                {/* Provider */}
                                <Field name="provider">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Provider
                                                {meta.touched && meta.error && (
                                                    <span className="text-red-500 text-base font-bold ml-1">*</span>
                                                )}
                                            </label>
                                            <Select
                                                {...input}
                                                placeholder="Select Provider"
                                                options={constructOptions(
                                                    getProviderData?.filter(p => p?.status === 'active'),
                                                    "id",
                                                    "name",
                                                )}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                onChange={(option) => input.onChange(option)}
                                                isLoading={getListingLoading}
                                            />
                                        </div>
                                    )}
                                </Field>

                                {/* Member Type */}
                                <Field name="memberType">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Type
                                                {meta.touched && meta.error && (
                                                    <span className="text-red-500 text-base font-bold ml-1">*</span>
                                                )}
                                            </label>
                                            <Select
                                                {...input}
                                                placeholder="Select Type"
                                                options={constructOptions(
                                                    getDataType?.filter(p => p?.status === 'active'),
                                                    "id",
                                                    "name",
                                                )}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                onChange={(option) => input.onChange(option)}
                                            />
                                        </div>
                                    )}
                                </Field>

                                {/* Status */}
                                <Field name="status">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Status
                                            </label>
                                            <Select
                                                {...input}
                                                placeholder="Select Status"
                                                options={options}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                onChange={(option) => input.onChange(option)}
                                                isLoading={getDataTypeLoading}
                                            />
                                        </div>
                                    )}
                                </Field>
                                {/* Zip Code */}
                                <Field name="zip_code">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Zip Code
                                            </label>
                                            <input
                                                {...input}
                                                type="text"
                                                placeholder="Enter Zip Code"
                                                maxLength={255}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}
                                </Field>

                                {/* Address */}
                                <Field name="address">
                                    {({ input, meta }) => (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Address
                                            </label>
                                            <textarea
                                        
                                        
                                                {...input}
                                                placeholder="Enter Address"
                                                maxLength={255}
                                                rows={4}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                             {meta.touched && meta.error && (
                                              <div className="text-red-500 text-sm mt-1">{meta.error}</div>
                                              )}
                                              
                                        </div>
                                    )}
                                </Field>
                            </div>

                            <div className="flex justify-end gap-4 pt-6">
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="px-5 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow transition"
                                >
                                    {id ? "Update" : 'Submit'}
                                </button>
                            </div>
                        </form>
                    )}
                />
            </div>
        </div>
    );
};

export default AddClient;